import React, { useState } from "react";
import PropTypes from "prop-types";
import { Heading, Text, Box, Flex, Image } from "@theme-ui/components";
import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import Carousel from "react-multi-carousel";
import MuxPlayer from '@mux/mux-player-react';
import Testimonial from "./Testimonial";
import { Tag, Button } from "../atoms";
import { colors, typography } from "../../styles/theme";
import "react-multi-carousel/lib/styles.css";
import "./heroSectioncarouselStyles.css";
import hero1 from "../../../static/images/hero-section/request-demo-page-hero.png";
import hero2 from "../../../static/images/hero-section/partners-page-hero.png";
import hero3 from "../../../static/images/hero-section/help-page-hero.png";
import hero4 from "../../../static/images/hero-section/watch-now-page-hero.png";
import hero5 from "../../../static/images/hero-section/hr-garden-page-hero.png";

const responsive = {
  allDevices: {
    breakpoint: { max: 3000, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};

const CustomDot = ({ onClick, active }) => (
  <button
    className={`custom-dot ${active ? "active" : ""}`}
    onClick={() => onClick()}
    style={{
      ...customDotStyles,
      width: active ? "13px" : "10px",
      height: active ? "13px" : "10px",
      backgroundColor: active ? colors.primaryMain : colors.primary200,
    }}
  ></button>
);

const HeroSection = ({
  variant,
  circleVariant,
  imagePath,
  bgColor,
  testimonialsData,
  image,
  videoUrl,
  thumbnailImage,
  youtubeId,
  tagLabel,
  tagLeftIcon,
  tagRightIcon,
  tagColor,
  heading,
  subHeading,
  subTitle,
  noteDesc,
  primaryBtnLabel,
  primaryButtonIconName,
  primaryBtnId = "hero-section-btn",
  secondaryBtnLabel,
  onPrimaryBtnClick,
  onSecondaryBtnClick,
  secondaryBtnId = "hero-section-secondary-btn",
  children,
  muxPlaybackId = "1U1Ni01yGbnkueKNHQigjwEXkzUgb01niy",
  customStyles
}) => {
  let heroImage = null;
  if (!imagePath && variant !== "infoTileHero" && image && Object.keys(image).length > 0) {
    heroImage = getImage(image);
  }

  // The below imageArray object is utilized to display images in Storybook. Add your image to this array only if you intend to showcase it in Storybook.
  const imageArray = {
    image1: hero1,
    image2: hero2,
    image3: hero3,
    image4: hero4,
    image5: hero5,
  };

  const [isPlaying, setIsPlaying] = useState(false);

  const handleThumbnailClick = () => {
    setIsPlaying(true);
  };

  if (variant === "testimonialCarousel") {
    return (
      <Flex
        sx={{
          backgroundColor: bgColor,
          ...primaryBox,
          ...customStyles
        }}
      >
        <Carousel
          swipeable={true}
          draggable={false}
          showDots={true}
          responsive={responsive}
          infinite={true}
          autoPlay={true}
          autoPlaySpeed={3000}
          keyBoardControl={true}
          customTransition="all .5"
          transitionDuration={500}
          containerClass="carousel-container"
          arrows={false}
          customDot={<CustomDot />}
          dotListClass="custom-dot-list"
        >
          {testimonialsData.map((testimonial, index) => (
            <Testimonial
              key={`testimonial-${index}`}
              reviewUrl={testimonial?.slug}
              variant="default"
              image={testimonial?.testimonialImage && testimonial?.testimonialImage?.gatsbyImageData}
              imageUrl={testimonial?.imageUrl}
              imageAltText={testimonial?.imageUrl ? testimonial?.imageAltText : testimonial?.testimonialImageAltText}
              companyLogo={testimonial?.companyLogo && testimonial?.companyLogo?.gatsbyImageData}
              companyLogoUrl={testimonial?.companyLogoUrl}
              companyLogoAltText={testimonial?.companyLogoAltText}
              feedback={testimonial?.feedback}
              employeeName={testimonial?.employeeName}
              employeeDesignation={testimonial?.employeeDesignation}
              rating={testimonial?.rating}
              customStyles={{ mx: "auto", }}
            />
          ))}
        </Carousel>

        <Flex sx={primaryImg}>
          <CircleVariantRenderer circleVariant={circleVariant} />
          {imagePath ? (
            <Image
              className="gatsby-image-container"
              src={imageArray[imagePath]}
              alt="Hero Image"
              sx={imagePathStyles}
            />
          ) : (
            <GatsbyImage
              image={heroImage}
              className="gatsby-image-container"
              loading="eager"
              imgStyle={{ height: "100%", objectFit: "contain" }}
              alt="Hero Image"
            />
          )}
        </Flex>
      </Flex>
    );
  } else if (variant === "default") {
    return (
      <Flex
        sx={{
          backgroundColor: bgColor,
          ...defaultBox,
          ...customStyles
        }}
      >
        <Flex sx={commonContainer}>
          <Flex sx={commonInfoBox}>
            {tagLabel && (
              <Tag
                label={tagLabel}
                leftIconName={tagLeftIcon}
                rightIconName={tagRightIcon}
                bgColor={tagColor}
                customStyles={{ mb: "1.5rem" }}
              />
            )}

            <Box
              sx={{
                width: "100%",
                "@media screen and (max-width: 1200px)": {
                  textAlign: "center",
                },
              }}
            >
              <Heading as="h1" sx={commonHeadingStyles}>{heading}</Heading>
              <Text sx={commonSubHeadingStyles}>{subHeading}</Text>
            </Box>
            <Flex sx={commonBtnContainer}>
              {primaryBtnLabel && (
                <Button
                  size="large"
                  onClick={onPrimaryBtnClick}
                  variant="filled"
                  id={primaryBtnId}
                >
                  {primaryBtnLabel}
                </Button>
              )}
              {secondaryBtnLabel && (
                <Button
                  size="large"
                  onClick={onSecondaryBtnClick}
                  variant="outlined"
                  id={secondaryBtnId}
                >
                  {secondaryBtnLabel}
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>

        <Flex sx={defaultImg}>
          <CircleVariantRenderer circleVariant={circleVariant} />
          {imagePath ? (
            <Image
              className="gatsby-image-container"
              src={imageArray[imagePath]}
              alt="Hero Image"
              sx={imagePathStyles}
            />
          ) : (
            <GatsbyImage
              image={heroImage}
              className="gatsby-image-container"
              imgStyle={{ height: "100%", objectFit: "contain" }}
              alt="Hero Image"
              loading="eager"
            />
          )}
        </Flex>
      </Flex>
    );
  } else if (variant === "infoTileHero") {
    return (
      <Flex
        sx={{
          backgroundColor: bgColor,
          ...secondaryBox,
          ...customStyles
        }}
      >
        <Flex sx={commonContainer}>
          <Flex sx={commonInfoBox}>
            {tagLabel && (
              <Tag
                label={tagLabel}
                leftIconName={tagLeftIcon}
                rightIconName={tagRightIcon}
                bgColor={colors.secondaryGreen200}
                customStyles={{ mb: "1.5rem" }}
              />
            )}
            <Box
              sx={{
                width: "100%",
                "@media screen and (max-width: 1200px)": {
                  textAlign: "center",
                },
              }}
            >
              <Heading as="h1" sx={commonHeadingStyles}>{heading}</Heading>
              <Text sx={commonSubHeadingStyles}>{subHeading}</Text>
            </Box>
            <Flex sx={commonBtnContainer}>
              {primaryBtnLabel && (
                <Button size="large" onClick={onPrimaryBtnClick} variant="filled" id={primaryBtnId}>
                  {primaryBtnLabel}
                </Button>
              )}
              {secondaryBtnLabel && (
                <Button size="large" onClick={onSecondaryBtnClick} variant="outlined" id={secondaryBtnId}>
                  {secondaryBtnLabel}
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex sx={infoTileContainer}>
          <Box sx={circle1Styles} />
          <Box sx={circle2Styles} />
          {children}
        </Flex>
      </Flex>
    );
  } else if (variant === "customContent") {
    return (
      <Flex
        sx={{
          backgroundColor: bgColor,
          ...defaultBox,
          ...customStyles
        }}
      >
        <Flex sx={customContainer}>
          {children}
        </Flex>

        <Flex sx={defaultImg}>
          <CircleVariantRenderer circleVariant={circleVariant} />
          {imagePath ? (
            <Image
              className="gatsby-image-container"
              src={imageArray[imagePath]}
              alt="Hero Image"
              sx={imagePathStyles}
            />
          ) : (
            <GatsbyImage
              image={heroImage}
              className="gatsby-image-container"
              imgStyle={{ height: "100%", objectFit: "contain" }}
              alt="Hero Image"
              loading="eager"
            />
          )}
        </Flex>
      </Flex>
    );
  } else if (variant === "CustomContentWithCenteredHeroImage") {
    return (
      <Flex
        sx={{
          backgroundColor: bgColor,
          ...defaultBox,
          ...customStyles,
        }}
      >
        <Flex sx={webinarContainer}>{children}</Flex>

        <Flex sx={webinarImg}>
          {imagePath ? (
            <Image
              className="gatsby-image-container"
              src={imageArray[imagePath]}
              alt="Hero Image"
              sx={imagePathStyles}
            />
          ) : (
            <GatsbyImage
              image={heroImage}
              className="gatsby-image-container"
              imgStyle={{ height: "100%", objectFit: "contain" }}
              alt="Hero Image"
              loading="eager"
            />
          )}
        </Flex>
      </Flex>
    );
  } else if (variant === "media") {
    const paddingBottomSytle =
      !imagePath && !videoUrl
        ? ["4rem", "4rem", "4.375rem", "4.5rem"]
        : ["2rem", "2rem", "5rem", "8rem"];
    return (
      <>
        <Flex
          sx={{
            backgroundColor: bgColor,
            ...defaultBox,
            px: ["2.625rem", "2.625rem", "4.375rem", "6.938rem"],
            paddingTop: ["4rem", "4rem", "4.375rem", "4.5rem"],
            paddingBottom: paddingBottomSytle,
            "@media (min-width: 900px) and (max-width: 1200px)": {
              paddingBottom: "5rem",
            },
            wordWrap: "break-word",
            ...customStyles,
          }}
        >
          <Flex sx={commonContainer}>
            <Flex
              sx={{
                ...commonInfoBox,
                pl: 0,
                py: 0,
                alignItems: "center",
                textAlign: "center",
              }}
            >
              {tagLabel && (
                <Tag
                  label={tagLabel}
                  leftIconName={tagLeftIcon}
                  rightIconName={tagRightIcon}
                  bgColor={tagColor}
                  customStyles={{ mb: ["1rem", "1rem", "1rem", "1.5rem"] }}
                />
              )}

              <Box
                sx={{
                  width: "100%",
                  "@media screen and (max-width: 1200px)": {
                    textAlign: "center",
                  },
                }}
              >
                <Box sx={{ mb: ["1rem", "1rem", "1rem", "1.5rem"] }}>
                  <Heading
                    as="h1"
                    sx={{ ...commonHeadingStyles, mb: "0.75rem" }}
                  >
                    {heading}
                  </Heading>
                  <Heading as="h3" sx={typography.h3}>
                    {subTitle}
                  </Heading>
                </Box>
                {subHeading && (
                  <Text
                    sx={{
                      ...commonSubHeadingStyles,
                      mb: ["1.5rem", "1.5rem", "1.5rem", "2rem"],
                    }}
                  >
                    {subHeading}
                  </Text>
                )}
                {children}
              </Box>
              <Flex
                sx={{
                  ...commonBtnContainer,
                  mb: noteDesc ? "0.75rem" : ["2rem", "2rem", "3rem", "3rem"],
                  gap: "1.5rem",
                  "@media(min-width: 1201px)": {
                    pb: (youtubeId && isPlaying) || !videoUrl ? "4rem" : 0,
                  },
                }}
              >
                {primaryBtnLabel && (
                  <Button
                    size="large"
                    variant="filled"
                    id={primaryBtnId}
                    iconName={primaryButtonIconName}
                    direction="ltr"
                    onClick={onPrimaryBtnClick}
                  >
                    {primaryBtnLabel}
                  </Button>
                )}
                {secondaryBtnLabel && (
                  <Button
                    size="large"
                    variant="outlined"
                    id={secondaryBtnId}
                    onClick={onSecondaryBtnClick}
                  >
                    {secondaryBtnLabel}
                  </Button>
                )}
              </Flex>
              {noteDesc && (
                <Text
                  sx={{
                    ...typography.textSm,
                    mb: ["2rem", "2rem", "4rem", "4rem"],
                    color: colors.neutral900,
                  }}
                >
                  {noteDesc}
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Box sx={mediaBoxStyles}>
          {youtubeId && isPlaying ? (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                paddingTop: "56.25%",
                overflow: "hidden",
                borderRadius: "0.75rem",
              }}
            >
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&loop=1`}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: "0.75rem",
                  border: "none",
                }}
              ></iframe>
            </Box>
          ) : (
            !videoUrl && (
              <Box
                onClick={handleThumbnailClick}
                sx={{
                  borderRadius: "0.75rem",
                }}
              >
                <GatsbyImage
                  image={getImage(thumbnailImage)}
                  alt="Thumbnail Image"
                  style={{ width: "100%", cursor: "pointer" }}
                />
              </Box>
            )
          )}

          {videoUrl && (
            <MuxPlayer
              width="100%"
              height="100%"
              playbackId={muxPlaybackId}
              autoPlay={true}
              loop={true}
              muted={true}
              poster={`https://image.mux.com/${muxPlaybackId}/thumbnail.webp?width=1800&height=900&fit_mode=crop&time=1`}
              preload="auto"
            />
          )}

          {imagePath && (
            <Image
              className="gatsby-image-container"
              src={imagePath}
              alt="Hero Image"
              sx={{
                ...imagePathStyles,
                borderRadius: "0.75rem",
                objectFit: "contain",
              }}
              imgStyle={{ height: "100%" }}
            />
          )}
        </Box>
      </>
    );
  } else if (variant === "mediaV2") {
    return (
      <Flex
        sx={{
          flexDirection: ["column", "column", "column", "row"],
          pt: ["3rem", "3rem", "4rem", "4rem"],
          maxWidth: "105rem",
          margin: "auto",
          ...customStyles,
        }}
      >
        <Box
          sx={{
            width: ["100%", "100%", "100%", "50%"],
            mr: ["0rem", "0rem", "0rem", "3rem"],
            "@media (max-width: 899px)": {
              textAlign: "center",
            },
          }}
        >
          <Heading
            as="h1"
            sx={{
              ...typography.h1,
              mb: "1.5rem",
              wordWrap: "break-word",
            }}
          >
            {heading}
          </Heading>
          {subHeading && (
            <Text
              sx={{
                ...typography.textLg400,
                color: colors.neutral1000,
              }}
            >
              {subHeading}
            </Text>
          )}
          <Flex sx={mediaV2BtnContainer}>
            {primaryBtnLabel && (
              <Button
                size="large"
                variant="filled"
                id={primaryBtnId}
                onClick={onPrimaryBtnClick}
                customStyles={{
                  width: "max-content"
                }}
              >
                {primaryBtnLabel}
              </Button>
            )}
            {secondaryBtnLabel && (
              <Button
                size="large"
                variant="outlined"
                id={secondaryBtnId}
                onClick={onSecondaryBtnClick}
                customStyles={{
                  width: "max-content"
                }}
              >
                {secondaryBtnLabel}
              </Button>
            )}
          </Flex>
        </Box>
        <Flex sx={{
          width: ["100%", "100%", "100%", "50%"],
          mt: ["3rem", "3rem", "3rem", "0rem"],
          alignItems: "center",
          ".hero-image-container": {
            mx: "auto",
            "@media (max-width: 599px)": {
              height: "45.063rem",
            },
          },
        }}>
          {videoUrl && <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            width="100%"
            height="440px"
          >
            <source src={videoUrl} type="video/mp4" />
          </video>}
          {imagePath ? (
            <Image
              className="hero-image-container"
              src={imagePath}
              alt="Hero Image"
              sx={{
                ...imagePathStyles,
                borderRadius: "0.75rem",
                objectFit: "contain",
                margin: "auto"
              }}
              imgStyle={{ height: "100%" }}
            />
          ) : (
            <GatsbyImage
              image={heroImage}
              className="hero-image-container"
              imgStyle={{
                width: "100%",
                mx: "auto",
                textAlign: "center",
                height: "100%",
                objectFit: "contain",
              }}
              alt="Hero Image"
            />
          )}
        </Flex>
      </Flex>
    );
  }
};

const CircleVariantRenderer = ({ circleVariant }) => {
  let RenderedComponent = null;

  switch (circleVariant) {
    case 'type1':
      RenderedComponent = StyledBoxes1;
      break;
    case 'type2':
      RenderedComponent = StyledBoxes2;
      break;
    case 'type3':
      RenderedComponent = StyledBoxes3;
      break;
    case 'type4':
      RenderedComponent = StyledBoxes4;
      break;
    default:
      RenderedComponent = () => <></>;
      break;
  }

  return <RenderedComponent />;
};

const StyledBoxes1 = () => {
  return (
    <>
      <Box
        sx={{
          position: "absolute",
          borderRadius: "50%",
          width: "150px",
          height: "150px",
          backgroundColor: colors.primaryMain,
          left: "30%",
          transform: "translate(-50%, -50%)",
          "@media screen and (max-width: 1500px)": {
            left: "25%",
          },
          "@media screen and (max-width: 1300px)": {
            left: "20%",
          },
          "@media screen and (max-width: 1200px)": {
            left: "38%",
            width: "120px",
            height: "120px",
          },
          "@media screen and (max-width: 1050px)": {
            left: "32%",
          },
          "@media screen and (max-width: 900px)": {
            left: "27%",
          },
          "@media screen and (max-width: 750px)": {
            left: "22%",
          },
          "@media screen and (max-width: 600px)": {
            left: "27%",
          },
          "@media screen and (max-width: 500px)": {
            left: "22%",
          },
          "@media screen and (max-width: 450px)": {
            left: "17%",
          },
          "@media screen and (max-width: 400px)": {
            left: "20%",
          },
        }}
      ></Box>
      <Box
        sx={{
          position: "absolute",
          borderRadius: "50%",
          width: "29px",
          height: "29px",
          backgroundColor: colors.primaryMain,
          top: "calc(100% - 18rem)",
          left: "32%",
          transform: "translate(-50%, -50%)",
          "@media screen and (max-width: 1500px)": {
            left: "27%",
          },
          "@media screen and (max-width: 1300px)": {
            left: "22%",
          },
          "@media screen and (max-width: 1200px)": {
            width: "24px",
            height: "24px",
            left: "40%",
            top: "calc(100% - 15rem)",
          },
          "@media screen and (max-width: 1050px)": {
            left: "35%",
          },
          "@media screen and (max-width: 900px)": {
            left: "30%",
          },
          "@media screen and (max-width: 750px)": {
            left: "25%",
          },
          "@media screen and (max-width: 700px)": {
            left: "30%",
            top: "calc(100% - 14rem)",
          },
          "@media screen and (max-width: 600px)": {
            left: "35%",
          },
          "@media screen and (max-width: 500px)": {
            left: "30%",
          },
          "@media screen and (max-width: 450px)": {
            left: "25%",
          },
        }}
      ></Box>
      <Box
        sx={{
          position: "absolute",
          borderRadius: "50%",
          width: "290px",
          height: "290px",
          backgroundColor: colors.primaryMain,
          top: "calc(100% - 18rem)",
          left: "58%",
          transform: "translate(-50%, -50%)",
          "@media screen and (max-width: 1500px)": {
            left: "60%",
          },
          "@media screen and (max-width: 1300px)": {
            left: "62%",
          },
          "@media screen and (max-width: 1200px)": {
            width: "240px",
            height: "240px",
            left: "56%",
            top: "calc(100% - 15rem)",
          },
          "@media screen and (max-width: 700px)": {
            width: "200px",
            height: "200px",
            left: "60%",
            top: "calc(100% - 13rem)",
          },
          "@media screen and (max-width: 400px)": {
            width: "180px",
            height: "180px",
            left: "64%",
            top: "calc(100% - 13.5rem)",
          },
        }}
      ></Box>
    </>
  );
}

const StyledBoxes2 = () => {
  return (
    <>
      <Box
        sx={{
          position: "absolute",
          overflow: "hidden",
          width: "530px",
          height: "420px",
          top: "calc(100% - 13.2rem)",
          left: "50%",
          transform: "translate(-50%, -50%)",
          "@media screen and (max-width: 1300px)": {
            width: "500px",
          },
          "@media screen and (max-width: 1200px)": {
            height: "370px",
            width: "470px",
            top: "calc(100% - 11.6rem)",
          },
          "@media screen and (max-width: 700px)": {
            maxWidth: "400px",
            width: "100%",
            height: "100%",
            top: "50%",
          },
        }}
      >
        <Box
          sx={{
            borderRadius: "50%",
            width: "100%",
            height: "120%",
            backgroundColor: colors.primaryMain,
            "@media screen and (max-width: 600px)": {
              width: "100%",
              height: "120%",
            },
          }}
        ></Box>
      </Box>

      <Box
        sx={{
          position: "absolute",
          borderRadius: "50%",
          width: "37px",
          height: "37px",
          backgroundColor: colors.primaryMain,
          top: "calc(100% - 25rem)",
          left: "75%",
          transform: "translate(-50%, -50%)",
          "@media screen and (max-width: 1500px)": {
            left: "80%",
          },
          "@media screen and (max-width: 1350px)": {
            left: "84%",
          },
          "@media screen and (max-width: 1200px)": {
            width: "24px",
            height: "24px",
            left: "70%",
            top: "calc(100% - 22rem)",
          },
          "@media screen and (max-width: 1000px)": {
            left: "75%",
          },
          "@media screen and (max-width: 900px)": {
            left: "80%",
          },
          "@media screen and (max-width: 820px)": {
            left: "85%",
          },
          "@media screen and (max-width: 750px)": {
            left: "90%",
          },
          "@media screen and (max-width: 700px)": {
            top: "calc(100% - 18rem)",
            left: "90%",
          },
          "@media screen and (max-width: 600px)": {
            width: "16px",
            height: "16px",
            top: 0,
            left: "75%",
          },
          "@media screen and (max-width: 500px)": {
            left: "80%",
          },
          "@media screen and (max-width: 400px)": {
            left: "85%",
            width: "14px",
            height: "14px",
          },
        }}
      ></Box>

      <Box
        sx={{
          position: "absolute",
          borderRadius: "50%",
          width: "60px",
          height: "60px",
          backgroundColor: colors.primaryMain,
          top: "calc(100% - 22rem)",
          left: "85%",
          transform: "translate(-50%, -50%)",
          "@media screen and (max-width: 1500px)": {
            left: "90%",
          },
          "@media screen and (max-width: 1350px)": {
            left: "94%",
          },
          "@media screen and (max-width: 1200px)": {
            width: "54px",
            height: "54px",
            left: "77%",
            top: "calc(100% - 19rem)",
          },
          "@media screen and (max-width: 1000px)": {
            left: "82%",
          },
          "@media screen and (max-width: 900px)": {
            left: "87%",
          },
          "@media screen and (max-width: 820px)": {
            left: "92%",
          },
          "@media screen and (max-width: 750px)": {
            left: "97%",
          },
          "@media screen and (max-width: 700px)": {
            top: "calc(100% - 15rem)",
            left: "100%",
          },
          "@media screen and (max-width: 600px)": {
            width: "44px",
            height: "44px",
            top: "10%",
            left: "85%",
          },
          "@media screen and (max-width: 500px)": {
            left: "90%",
          },
          "@media screen and (max-width: 400px)": {
            width: "34px",
            height: "34px",
            left: "95%",
          },
        }}
      ></Box>
    </>
  );
}

const StyledBoxes3 = () => {
  return (
    <>
      <Box
        sx={{
          position: "absolute",
          width: "330px",
          height: "330px",
          borderRadius: "50%",
          backgroundColor: colors.primaryMain,
          top: "calc(100% - 22rem)",
          left: "50%",
          transform: "translate(-50%, -50%)",
          "@media screen and (max-width: 1200px)": {
            height: "250px",
            width: "250px",
            top: "calc(100% - 15rem)",
          },
          "@media screen and (max-width: 700px)": {
            height: "200px",
            width: "200px",
            top: "calc(100% - 13rem)",
          },
        }}
      ></Box>

      <Box
        sx={{
          position: "absolute",
          borderRadius: "50%",
          width: "37px",
          height: "37px",
          backgroundColor: colors.primaryMain,
          top: "calc(100% - 30.5rem)",
          left: "30%",
          transform: "translate(-50%, -50%)",
          "@media screen and (max-width: 1500px)": {
            left: "23%",
          },
          "@media screen and (max-width: 1200px)": {
            left: "35%",
            top: "calc(100% - 21rem)",
          },
          "@media screen and (max-width: 950px)": {
            left: "30%",
          },
          "@media screen and (max-width: 800px)": {
            left: "25%",
          },
          "@media screen and (max-width: 700px)": {
            width: "27px",
            height: "27px",
            top: "calc(100% - 18rem)",
          },
          "@media screen and (max-width: 400px)": {
            left: "20%",
          },
        }}
      ></Box>
    </>
  );
}

const StyledBoxes4 = () => {
  return (
    <>
      <Box
        sx={{
          position: "absolute",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          backgroundColor: colors.primaryMain,
          top: "calc(100% - 16rem)",
          left: "50%",
          transform: "translate(-50%, -50%)",
          "@media screen and (max-width: 1200px)": {
            height: "320px",
            width: "320px",
            top: "calc(100% - 10rem)",
          },
          "@media screen and (max-width: 700px)": {
            height: "250px",
            width: "250px",
            top: "calc(100% - 9rem)",
          },
        }}
      ></Box>
    </>
  );
}

const {
  customDotStyles,
  primaryBox,
  primaryImg,
  defaultBox,
  commonContainer,
  customContainer,
  webinarContainer,
  commonInfoBox,
  commonHeadingStyles,
  commonSubHeadingStyles,
  commonBtnContainer,
  defaultImg,
  webinarImg,
  imagePathStyles,
  secondaryBox,
  infoTileContainer,
  circle1Styles,
  circle2Styles,
  mediaBoxStyles,
  mediaV2BtnContainer,
} = {
  customDotStyles: {
    margin: "0 5px",
    borderRadius: "50%",
    border: "none",
    outline: "none",
  },
  primaryBox: {
    borderRadius: "24px",
    flexDirection: "row",
    "@media screen and (max-width: 1200px)": {
      flexDirection: "column",
    },
    pl: ["0.625rem", "0.625rem", "5rem", "3rem"],
    pr: ["0.625rem", "0.625rem", "5rem", 0],
    "@media screen and (min-width: 1800px)": {
      px: "10rem",
    },
    "@media screen and (min-width: 2200px)": {
      px: "20rem",
    },
    "@media screen and (min-width: 2400px)": {
      px: "30rem",
    },
  },
  primaryImg: {
    position: "relative",
    width: "100%",
    justifyContent: "center",
    alignItems: "flex-end",
    pt: "2.75rem",
    "@media screen and (max-width: 1200px)": {
      pt: 0,
    },
  },
  defaultBox: {
    borderRadius: "24px",
    flexDirection: "row",
    "@media screen and (max-width: 1200px)": {
      flexDirection: "column",
    },
    px: ["0.625rem", "0.625rem", "5rem", "3rem"],
    "@media screen and (min-width: 1800px)": {
      px: "15rem",
    },
    "@media screen and (min-width: 2200px)": {
      px: "20rem",
    },
    "@media screen and (min-width: 2400px)": {
      px: "30rem",
    },
  },
  secondaryBox: {
    borderRadius: "24px",
    flexDirection: "row",
    "@media screen and (max-width: 1200px)": {
      flexDirection: "column",
    },
    px: ["0.625rem", "0.625rem", "5rem", "3rem"],
    "@media screen and (min-width: 1800px)": {
      px: "15rem",
    },
    "@media screen and (min-width: 2200px)": {
      px: "20rem",
    },
    "@media screen and (min-width: 2400px)": {
      px: "30rem",
    },
  },
  commonContainer: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  customContainer: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    py: "4rem",
    "@media screen and (max-width: 1200px)": {
      py: "2rem",
    },
  },
  webinarContainer: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    py: "7.25rem",
    pl: "5.75rem",
    "@media screen and (max-width: 1200px)": {
      pt: "4rem",
      pb: "2rem",
      pl: 0,
    },
  },
  commonInfoBox: {
    flexDirection: "column",
    pl: "5rem",
    py: "4rem",
    "@media screen and (max-width: 1200px)": {
      pl: 0,
      py: "2rem",
      alignItems: "center",
    },
  },
  commonSubHeadingStyles: {
    ...typography.textLg400,
    mb: "1.5rem",
    display: "block",
    "@media screen and (max-width: 600px)": {
      ...typography.textMd400,
    },
  },
  commonHeadingStyles: { ...typography.h1, mb: "1rem", display: "block" },
  commonBtnContainer: {
    gap: "1.125rem",
    flexDirection: ["column", "column", "column", "row"],
  },
  defaultImg: {
    position: "relative",
    width: "100%",
    justifyContent: "center",
    pt: "2.75rem",
    alignItems: "flex-end",
    "@media screen and (max-width: 1200px)": {
      pt: 0,
    },
  },
  webinarImg: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    "@media screen and (max-width: 1200px)": {
      pb: "4rem",
    },
  },
  imagePathStyles: {
    width: "auto",
    zIndex: "1",
  },
  infoTileContainer: {
    width: "100%",
    minHeight: "31.25rem",
    position: "relative",
    pt: "5.125rem",
    pb: "2.875rem",
    justifyContent: "center",
    "@media screen and (max-width: 1200px)": {
      pt: "1rem",
      pb: "2rem",
    },
    "@media screen and (max-width: 600px)": {
      px: "2rem",
    },
    "@media screen and (max-width: 400px)": {
      px: "1rem",
    },
  },
  circle1Styles: {
    width: "9.3rem",
    height: "9.3rem",
    borderRadius: "50%",
    backgroundColor: colors.primaryMain,
    position: "absolute",
    top: "75%",
    left: "27%",
    transform: "translate(-50%, -50%)",
    "@media screen and (max-width: 1600px)": {
      left: "24%",
    },
    "@media screen and (max-width: 1400px)": {
      left: "21%",
    },
    "@media screen and (max-width: 1300px)": {
      left: "17%",
    },
    "@media screen and (max-width: 1200px)": {
      left: "31%",
    },
    "@media screen and (max-width: 1050px)": {
      left: "28%",
    },
    "@media screen and (max-width: 900px)": {
      left: "22%",
    },
    "@media screen and (max-width: 800px)": {
      left: "16%",
    },
    "@media screen and (max-width: 700px)": {
      left: "8%",
    },
    "@media screen and (max-width: 600px)": {
      left: "18%",
    },
    "@media screen and (max-width: 400px)": {
      left: "22%",
    },
    "@media screen and (max-width: 360px)": {
      left: "24%",
    },
  },
  circle2Styles: {
    width: "21rem",
    height: "21rem",
    borderRadius: "50%",
    backgroundColor: colors.primaryMain,
    position: "absolute",
    top: "33%",
    left: "59%",
    transform: "translate(-50%, -50%)",
    "@media screen and (max-width: 1600px)": {
      left: "61%",
    },
    "@media screen and (max-width: 1400px)": {
      left: "63%",
    },
    "@media screen and (max-width: 1300px)": {
      left: "64%",
    },
    "@media screen and (max-width: 1200px)": {
      top: "30%",
    },
    "@media screen and (max-width: 900px)": {
      left: "70%",
    },
    "@media screen and (max-width: 700px)": {
      left: "77%",
    },
    "@media screen and (max-width: 600px)": {
      left: "63%",
    },
    "@media screen and (max-width: 490px)": {
      left: "58%",
    },
    "@media screen and (max-width: 440px)": {
      left: "54%",
    },
    "@media screen and (max-width: 400px)": {
      left: "72%",
      top: "18%",
      width: "12rem",
      height: "12rem",
    },
    "@media screen and (max-width: 360px)": {
      left: "69%",
    },
  },
  mediaBoxStyles: {
    width: ["95%", "95%", "95%", "85%"],
    minHeight: ["11.875rem", "11.875rem", "20rem", "26.25rem"],
    borderRadius: "0.75rem",
    overflow: "hidden",
    objectFit: "cover",
    border: "none",
    position: "relative",
    mx: "auto",
    top: ["-4rem", "-4rem", "-7rem"],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "mux-player": {
      "--controls": "none",
    },
  },
  mediaV2BtnContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    mt: ["3rem", "3rem", "3rem", "4rem"],
    gap: "1.5rem",
    "@media (max-width: 899px)": {
      mt: "3rem",
      flexWrap: "nowrap",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
  },
};

HeroSection.propTypes = {
  variant: PropTypes.oneOf(["testimonialCarousel", "default", "infoTileHero", "customContent", "CustomContentWithCenteredHeroImage", "media", "mediaV2"]).isRequired,
  circleVariant: PropTypes.oneOf(["type1", "type2", "type3", "type4"]),
  onPrimaryBtnClick: PropTypes.func,
  onSecondaryBtnClick: PropTypes.func,
  tagLeftIcon: PropTypes.string,
  tagRightIcon: PropTypes.string,
  primaryBtnLabel: PropTypes.string,
  primaryBtnId: PropTypes.string,
  secondaryBtnLabel: PropTypes.string,
  secondaryBtnId: PropTypes.string,
  imagePath: PropTypes.string,
  heading: PropTypes.string,
  subHeading: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  bgColor: PropTypes.string,
  testimonialsData: PropTypes.arrayOf(
    PropTypes.shape({
      variant: PropTypes.string,
      imageUrl: PropTypes.string,
      image: PropTypes.string,
      imageAltText: PropTypes.string,
      feedback: PropTypes.string,
      employeeName: PropTypes.string,
      employeeDesignation: PropTypes.string,
      rating: PropTypes.number,
    }),
  ),
  tagLabel: PropTypes.string,
  tagColor: PropTypes.string,
  children: PropTypes.node,
  image: PropTypes.object,
  customStyles: PropTypes.object,
  muxPlaybackId: PropTypes.string,
};

export default HeroSection;
